import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useColorModeValue,
  FormErrorMessage,
  useToast,
  Container,
  Image,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const LoginPage: React.FC = () => {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Auth context
  const { login, error, loading, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const pageBgColor = useColorModeValue("gray.50", "gray.900");

  // Redirect if already logged in
  useEffect(() => {
    if (user && !loading) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  // Show loading spinner during auth check
  if (loading) {
    return (
      <Flex minH={"100vh"} align={"center"} justify={"center"} bg={pageBgColor}>
        <Container maxW={"lg"} py={12} px={{ base: 4, sm: 8 }}>
          <Stack align={"center"}>
            <Text>Loading...</Text>
          </Stack>
        </Container>
      </Flex>
    );
  }

  // Don't render login form if user is already authenticated
  if (user) {
    return null;
  }

  // Form validation
  const validateForm = () => {
    let isValid = true;

    // Email validation
    if (!email) {
      setEmailError("Email wajib diisi");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Email tidak valid");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Password validation
    if (!password) {
      setPasswordError("Password wajib diisi");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Password minimal 6 karakter");
      isValid = false;
    } else {
      setPasswordError("");
    }

    return isValid;
  };

  // Handle login
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        await login(email, password);
      } catch (err) {
        console.error("Login error:", err);
        toast({
          title: "Login Gagal",
          description: error || "Terjadi kesalahan saat login",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Flex
      minH={"100vh"}
      align={"top"}
      justify={"center"}
      bg={pageBgColor}
    >
      <Container maxW={"lg"} py={12} px={{ base: 4, sm: 8 }}>
        <Stack spacing={8}>
          {/* Logo and heading */}
          <Stack align={"center"}>
            <Image
              src="/GBS-Logo-Green-Yellow.png"
              alt="GBS Logo"
              boxSize="100px"
              objectFit="contain"
            />
            <Heading fontSize={"3xl"} textAlign={"center"} color="green.600">
              PIJAR
            </Heading>
            <Text fontSize={"lg"} color={textColor} textAlign="center">
              Silakan login untuk mengakses dashboard
            </Text>
          </Stack>

          {/* Login form */}
          <Box
            rounded={"lg"}
            bg={bgColor}
            boxShadow={"lg"}
            p={8}
            border="1px"
            borderColor={borderColor}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {/* Email input */}
                <FormControl id="email" isInvalid={!!emailError}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Masukan email"
                  />
                  <FormErrorMessage>{emailError}</FormErrorMessage>
                </FormControl>

                {/* Password input */}
                <FormControl id="password" isInvalid={!!passwordError}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Masukan password"
                    />
                    <InputRightElement h={"full"}>
                      <Button
                        variant={"ghost"}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{passwordError}</FormErrorMessage>
                </FormControl>

                {/* Error message */}
                {error && (
                  <Text color="red.500" fontSize="sm">
                    {error}
                  </Text>
                )}

                {/* Login button */}
                <Button
                  bg={"green.500"}
                  color={"white"}
                  _hover={{
                    bg: "green.600",
                  }}
                  type="submit"
                  isLoading={loading}
                  loadingText="Logging in..."
                  size="lg"
                  fontSize="md"
                  mt={2}
                >
                  Login
                </Button>
              </Stack>
            </form>
          </Box>

          {/* Footer */}
          {/* <Text fontSize={"sm"} color={textColor} textAlign="center">
            &copy; {new Date().getFullYear()} GBS Insight. All rights reserved.
          </Text> */}
        </Stack>
      </Container>
    </Flex>
  );
};

export default LoginPage;
